/* TurnWizard admin — everything that talks to Supabase.
 *
 * The console's sync.js exists because a salon tablet has to keep working on
 * flaky wifi: writes are optimistic, queued in localStorage and replayed. None
 * of that applies here. This app is used at a desk on a connection that works,
 * by somebody who needs to know whether a change to a customer's settings
 * actually landed — so every write is awaited, and a failure is shown rather
 * than retried behind the scenes. An admin who is told "saved" about a write
 * that is still sitting in a queue is being lied to about another business's
 * configuration.
 *
 * ── HOW THIS IS ALLOWED TO SEE EVERY SALON ──────────────────────────────────
 *
 * The same publishable key the console ships, and an ordinary email sign-in. The
 * privilege is not in the key or in this file: it is the caller's row in
 * `platform_admins`, which every policy in
 * admin/server/schema/11-admin-policies.sql reads through
 * `private.is_platform_admin()`. Take that row away and this app can read
 * exactly one salon, or none.
 *
 * Four things the policies cannot reach — auth.users, the ingest key table, the
 * vault password, and creating or deleting a tenant — go through the `admin-api`
 * edge function instead. `call()` at the bottom is the whole of that.
 */
import * as Vue from 'vue';
import { createClient } from '@supabase/supabase-js';

var cfg = (window.TurnWizardAdmin && window.TurnWizardAdmin.config) || null;

/* Same failure mode as the console's, and the same answer: without a config this
 * module throws, everything downstream of it throws, Vue never mounts, and the
 * page is white with a stack in the console that names the wrong file. Say what
 * is wrong on the page instead, outside #app so Vue clearing its own container
 * does not take the message with it.
 */
function unconfigured() {
  var el = document.createElement('div');
  el.setAttribute('style', 'position:fixed;inset:0;z-index:9999;display:flex;' +
    'align-items:center;justify-content:center;padding:24px;background:#fff;' +
    'color:#1a1a1a;font:16px/1.7 system-ui,-apple-system,sans-serif');
  el.innerHTML =
    '<div style="max-width:44em">' +
    '<h1 style="font-size:1.4em;margin:0 0 .6em">Admin is not configured</h1>' +
    '<p style="margin:0 0 1em"><code>js/config.js</code> is missing, or still ' +
    'holds the placeholder values it shipped with.</p>' +
    '<p style="margin:0">Copy <code>admin/public/js/config.example.js</code> to ' +
    '<code>admin/public/js/config.js</code> and fill in the Supabase URL and ' +
    'publishable key. They are the same two values the console uses.</p>' +
    '</div>';
  document.body.appendChild(el);
  console.error('Admin is not configured: js/config.js is missing or still ' +
    'holds placeholder values. The errors below this line are downstream of it.');
}

if (!cfg || !cfg.supabaseUrl || !cfg.supabaseKey ||
    cfg.supabaseUrl.indexOf('YOUR_') !== -1 || cfg.supabaseKey.indexOf('YOUR_') !== -1) {
  unconfigured();
  throw new Error('Admin is not configured — see admin/public/js/config.example.js');
}

/* The storage key is the one line in this file that is load-bearing for the
 * console rather than for this app.
 *
 * supabase-js defaults to `sb-<project-ref>-auth-token` in localStorage, which
 * is scoped to the ORIGIN and not to the path. Deploy this build to /admin/ on
 * the host the console is served from — the obvious thing to do — and both apps
 * would read and write that one key. Signing in here as an admin would replace
 * the station's session on the tablet the board is open on, and the board would
 * start reading whichever salon the admin belongs to, or none at all.
 *
 * Naming our own key gives the two apps separate sessions on one origin. It is
 * also why signing out here does not sign the board out, which is the behaviour
 * anybody would expect and would otherwise not get.
 */
var sb = createClient(cfg.supabaseUrl, cfg.supabaseKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    storageKey: 'turnwizard.admin.auth.v1'
  },
  /* No realtime. The console subscribes because a board on the wall has to
   * follow a board at the desk; nothing here is looking at the same row as
   * somebody else, and a subscription across every tenant is a firehose that
   * would exist to redraw a form nobody is watching. Pages reload what they
   * changed. */
  realtime: { params: { eventsPerSecond: 1 } }
});

/* ---------------- who is signed in ----------------
 *
 * `isAdmin` is resolved by asking the database rather than by reading a claim,
 * because the database is what enforces it. A `select` against platform_admins
 * comes back with a row under its own read policy only for an admin, so the
 * lookup and the grant are the same fact and cannot drift apart. A station that
 * signs in here sees the refusal screen, and would see nothing but empty tables
 * even if it did not.
 */
var auth = Vue.reactive({
  user: null,
  isAdmin: false,
  checking: true,
  busy: false,
  error: null
});

async function resolveAdmin() {
  if (!auth.user) { auth.isAdmin = false; return; }
  try {
    var res = await sb.from('platform_admins')
      .select('user_id').eq('user_id', auth.user.id).maybeSingle();
    auth.isAdmin = !res.error && !!res.data;
  } catch (e) {
    auth.isAdmin = false;
  }
}

sb.auth.getSession().then(async function (res) {
  var session = res.data && res.data.session;
  auth.user = (session && session.user) || null;
  await resolveAdmin();
  auth.checking = false;
}).catch(function () { auth.checking = false; });

sb.auth.onAuthStateChange(function (_evt, session) {
  var next = (session && session.user) || null;
  var changed = (next && next.id) !== (auth.user && auth.user.id);
  auth.user = next;
  if (changed) resolveAdmin();
});

async function signIn(email, password) {
  auth.busy = true;
  auth.error = null;
  try {
    var res = await sb.auth.signInWithPassword({ email: email, password: password });
    if (res.error) { auth.error = res.error.message; return false; }
    auth.user = res.data.user;
    await resolveAdmin();
    return true;
  } catch (err) {
    auth.error = String(err && err.message ? err.message : err);
    return false;
  } finally {
    auth.busy = false;
  }
}

async function signOut() {
  try { await sb.auth.signOut(); } catch (e) { /* already gone */ }
  auth.user = null;
  auth.isAdmin = false;
}

/* ---------------- the status line ----------------
 *
 * One place for "saving…", "saved" and the last error, shown in the top bar. A
 * per-control spinner would be more precise and less useful: what an admin needs
 * to know is whether the last thing they typed reached the database, and that is
 * one question however many boxes are on the page.
 */
var status = Vue.reactive({
  busy: 0,
  savedAt: null,
  error: null,
  toast: null
});

var toastTimer = null;

function toast(text, kind) {
  status.toast = { text: text, kind: kind || 'ok' };
  if (toastTimer) clearTimeout(toastTimer);
  toastTimer = setTimeout(function () { status.toast = null; }, 4200);
}

// Wraps anything that talks to the server so the top bar knows something is in
// flight and so a thrown error lands in one place. Returns null on failure —
// every caller checks, and none of them has to write a try/catch.
async function run(label, fn) {
  status.busy += 1;
  status.error = null;
  try {
    var out = await fn();
    status.savedAt = Date.now();
    return out;
  } catch (err) {
    var message = String(err && err.message ? err.message : err);
    status.error = label + ': ' + message;
    toast(message, 'bad');
    return null;
  } finally {
    status.busy -= 1;
  }
}

function ok(res) {
  if (res.error) throw new Error(res.error.message);
  return res.data;
}

/* ---------------- reading ---------------- */

/* Every read names its tenant.
 *
 * Under the admin arm of the policies an unfiltered select on tt_turns returns
 * every salon's turns, which is never what a page wants and is a slow way to
 * find out. The filter is here rather than at each call site so that forgetting
 * it is not possible: `rows` takes the tenant first and appends `.eq` before the
 * caller's own conditions.
 *
 * `build` gets the query and may narrow or order it further.
 */
async function rows(table, tenantId, build) {
  return run('Loading ' + table, async function () {
    var q = sb.from(table).select('*').eq('tenant_id', tenantId);
    if (build) q = build(q);
    return ok(await q);
  });
}

// For the handful of tables that are not tenant-scoped — platform_admins,
// tt_channel_meta — and for the audit log, which is filtered by tenant only
// when a tenant is selected.
async function query(label, build) {
  return run(label, async function () { return ok(await build(sb)); });
}

async function rpc(name, args) {
  return run(name, async function () { return ok(await sb.rpc(name, args || {})); });
}

/* ---------------- writing ---------------- */

/* Stamp the tenant on the way out, always, and never take one off a form.
 *
 * The console does this in `enqueue` for 21 write sites and calls the chokepoint
 * the whole reason the queue exists. The same argument applies here for a
 * different reason: every tt_* table declares
 * `tenant_id uuid not null default private.auth_tenant_id()`, and a platform
 * admin has no membership, so that default is NULL. An insert that does not name
 * the tenant fails on the not-null constraint and reports itself as a broken
 * table rather than as a missing field. See the note in 11-admin-policies.sql.
 *
 * It is also the only defence against the worst mistake this app could make. A
 * page that edited one salon and wrote another's row would be a silent
 * cross-tenant write with full permission to do it — the policies cannot catch
 * it, because an admin is allowed to write both. So the tenant comes from the
 * selection, as an argument, at one place.
 */
async function writeRow(table, tenantId, row, onConflict) {
  return run('Saving ' + table, async function () {
    var payload = Object.assign({}, row, { tenant_id: tenantId });
    var q = sb.from(table).upsert(payload,
      onConflict ? { onConflict: onConflict } : undefined).select();
    return ok(await q);
  });
}

async function writeRows(table, tenantId, list, onConflict) {
  if (!list.length) return [];
  return run('Saving ' + table, async function () {
    var payload = list.map(function (r) {
      return Object.assign({}, r, { tenant_id: tenantId });
    });
    var q = sb.from(table).upsert(payload,
      onConflict ? { onConflict: onConflict } : undefined).select();
    return ok(await q);
  });
}

// `match` is ANDed onto the tenant filter, never instead of it. A delete with an
// empty match therefore clears one salon's table, which is a real operation the
// danger zone offers — but it cannot become every salon's table by omission.
async function deleteRows(table, tenantId, match) {
  return run('Deleting from ' + table, async function () {
    var q = sb.from(table).delete().eq('tenant_id', tenantId);
    Object.keys(match || {}).forEach(function (k) { q = q.eq(k, match[k]); });
    return ok(await q.select());
  });
}

// Straight update by primary key, for the tables whose rows the admin edits in
// place. Kept separate from `writeRow` because an upsert of a partial row would
// null every column the form does not carry.
async function updateRow(table, tenantId, match, patch) {
  return run('Saving ' + table, async function () {
    var q = sb.from(table).update(patch).eq('tenant_id', tenantId);
    Object.keys(match || {}).forEach(function (k) { q = q.eq(k, match[k]); });
    return ok(await q.select());
  });
}

/* ---------------- the audit log ---------------- */

/* Called after anything this app changes.
 *
 * Through the `admin_log` function rather than an insert, because the function
 * stamps the actor from the session — an insert from the browser would name its
 * own actor and a log that can name its own actor is not a log. It is fire and
 * forget: a change that happened and was not recorded is bad, and a change
 * refused because the recording failed is worse.
 */
function log(tenantId, action, detail) {
  sb.rpc('admin_log', {
    p_tenant: tenantId || null,
    p_action: action,
    p_detail: detail || {}
  }).then(function (res) {
    if (res.error) console.warn('audit: ' + res.error.message);
  });
}

/* ---------------- the edge function ---------------- */

/* The four things RLS cannot reach. See the header of
 * admin/server/supabase/functions/admin-api/index.ts for what they are and why each one
 * is there rather than here.
 *
 * `invoke` attaches the caller's session token on its own, which is what the
 * function verifies — there is no key of any kind in this call.
 */
async function call(action, body) {
  return run(action, async function () {
    var res = await sb.functions.invoke('admin-api',
      { body: Object.assign({ action: action }, body || {}) });
    /* A non-2xx from a function comes back as a FunctionsHttpError whose message
     * is "Edge Function returned a non-2xx status code" — true, and useless. The
     * handler's own JSON is in `context`, and that is where the sentence written
     * for a person to read actually is. */
    if (res.error) {
      var detail = null;
      try {
        detail = res.error.context && typeof res.error.context.json === 'function'
          ? await res.error.context.json() : null;
      } catch (e) { /* not JSON — the generic message stands */ }
      throw new Error((detail && detail.error) || res.error.message);
    }
    if (res.data && res.data.error) throw new Error(res.data.error);
    return res.data;
  });
}

export const api = {
  sb: sb,
  /* Where the console lives, if the deployment said. Empty is a real answer and
   * every caller handles it — see the note in config.example.js. */
  consoleUrl: String(cfg.consoleUrl || '').trim(),
  auth: auth,
  status: status,
  signIn: signIn,
  signOut: signOut,
  toast: toast,
  run: run,
  rows: rows,
  query: query,
  rpc: rpc,
  writeRow: writeRow,
  writeRows: writeRows,
  updateRow: updateRow,
  deleteRows: deleteRows,
  log: log,
  call: call
};
